import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";

import AdvancePaymentForm from "../components/AdvancePaymentForm";
import RefundReportView from "../components/RefundReportView";
import ReportEntryForm from "../components/ReportEntryForm";
import Header from "../components/molecules/Header";
import Loading from "../components/atoms/Loading";
import AuthContent from "../components/AuthContent";
import SessionExpiredDialog from "../components/SessionExpiredDialog";
import Collaborator from "../@types/Collaborator";
import { useLogged } from "../service/user";
import { SESSION_EXPIRED_EVENT } from "../service/api";
import ProfileAccessErrorScreen from "../components/ProfileAccessErrorScreen";

const AppRouter = () => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const hadSession = useRef(false);
  const lastLoggedUser = useRef<Collaborator | null>(null);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [preserveSessionContent, setPreserveSessionContent] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isAuthInvalid, setIsAuthInvalid] = useState(false);
  const [isRetryingProfile, setIsRetryingProfile] = useState(false);
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const { data: loggedUser, error, isFetching, isLoading, refetch } = useLogged(
    !isSigningOut && !isAuthInvalid && !preserveSessionContent,
  );
  const isUnauthorized = axios.isAxiosError(error) && error.response?.status === 401;
  const shouldInvalidateAuth = isUnauthorized && !preserveSessionContent;

  useEffect(() => {
    const showSessionExpiredDialog = () => {
      setIsSessionExpired(true);
      setPreserveSessionContent(true);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, showSessionExpiredDialog);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, showSessionExpiredDialog);
  }, []);

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !hadSession.current && !hasTriedSignin) {
      setHasTriedSignin(true);
      void auth.signinRedirect();
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.signinRedirect, hasTriedSignin]);

  useEffect(() => {
    if (auth.isLoading || isSigningOut) return;

    if (shouldInvalidateAuth && !isAuthInvalid) {
      setIsAuthInvalid(true);
      setPreserveSessionContent(false);
      setHasTriedSignin(true);
      hadSession.current = false;
      lastLoggedUser.current = null;
      queryClient.clear();
      void auth.removeUser();
    } else if (!auth.isAuthenticated && !preserveSessionContent && !isAuthInvalid) {
      if (hadSession.current) setHasTriedSignin(true);
      hadSession.current = false;
      lastLoggedUser.current = null;
      queryClient.clear();
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.removeUser, shouldInvalidateAuth, isAuthInvalid, isSigningOut, preserveSessionContent, queryClient]);

  const handleLogout = async () => {
    setIsSigningOut(true);
    setIsSessionExpired(false);
    setPreserveSessionContent(false);
    setHasTriedSignin(true);
    hadSession.current = false;
    lastLoggedUser.current = null;
    queryClient.clear();

    try {
      await auth.signoutRedirect();
    } catch {
      setIsSigningOut(false);
    }
  };

  if (auth.isAuthenticated && !isAuthInvalid) {
    hadSession.current = true;
  }

  if (auth.isAuthenticated && loggedUser && !shouldInvalidateAuth && !isAuthInvalid) {
    lastLoggedUser.current = loggedUser;
  }

  const userAccount = isSigningOut || isAuthInvalid || shouldInvalidateAuth
    ? null
    : preserveSessionContent
      ? loggedUser ?? lastLoggedUser.current
      : auth.isAuthenticated
        ? loggedUser
        : null;

  if (isSigningOut) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#f5f6f6]">
        <Loading />
      </main>
    );
  }

  if (auth.isLoading && !auth.isAuthenticated && !preserveSessionContent) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#f5f6f6]">
        <Loading />
      </main>
    );
  }

  if (isAuthInvalid || shouldInvalidateAuth || (!auth.isLoading && !auth.isAuthenticated && !preserveSessionContent)) {
    return <AuthContent />;
  }

  if (!hadSession.current) {
    if (auth.isLoading) {
      return (
        <main className="flex min-h-screen items-center justify-center bg-[#f5f6f6]">
          <Loading />
        </main>
      );
    }
    return <AuthContent />;
  }

  if (isLoading && !userAccount && !isRetryingProfile) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#f5f6f6]">
        <Loading />
      </main>
    );
  }

  if (!userAccount) {
    const isUserMissing = axios.isAxiosError(error) && error.response?.status === 404;
    return (
      <ProfileAccessErrorScreen
        isUserMissing={isUserMissing}
        isRetrying={isFetching || isRetryingProfile}
        onLogout={() => void handleLogout()}
        onRetry={() => {
          setIsRetryingProfile(true);
          void refetch().finally(() => setIsRetryingProfile(false));
        }}
      />
    );
  }

  return (
    <Router>
      <SessionExpiredDialog
        open={isSessionExpired}
        onStay={() => setIsSessionExpired(false)}
        onLoginAgain={() => void auth.signinRedirect()}
      />
      <Header account={userAccount} onLogout={() => void handleLogout()} />
      <Routes>
        <Route
          path="/"
          key="home"
          element={
            userAccount && (
              <RefundReportView
                account={userAccount}
                groupByCollaborator={false}
                selfRefundsOnly={true}
                title="Meus Reembolsos"
                showTotalValue={true}
                showExportFilesButtons={false}
                showSendToButtons={false}
                showAddReportEntryButton={true}
              />
            )
          }
        />

        <Route
          path="/approvals"
          key="approvals"
          element={
            userAccount ? (
              <RefundReportView
                account={userAccount}
                groupByCollaborator={true}
                selfRefundsOnly={false}
                title=""
                showTotalValue={false}
                showExportFilesButtons={false}
                showSendToButtons={false}
                showAddReportEntryButton={false}
                includeAdvances={false}
              />
            ) : (
              <div>Carregando...</div>
            )
          }
        />

        <Route
          path="/search"
          key="search"
          element={
            userAccount ? (
              <RefundReportView
                account={userAccount}
                groupByCollaborator={false}
                selfRefundsOnly={false}
                title="Reembolsos e Adiantamentos"
                showTotalValue={false}
                showExportFilesButtons={true}
                showSendToButtons={true}
                showAddReportEntryButton={false}
                includeAdvances={false}
              />
            ) : (
              <div>Carregando...</div>
            )
          }
        />

        <Route
          path="/new_request"
          key="new_request"
          element={userAccount && <ReportEntryForm account={userAccount} />}
        />

        <Route
          path="/edit_request"
          key="edit_request"
          element={userAccount && <ReportEntryForm account={userAccount} />}
        />

        <Route
          path="/new_advance_payment"
          key="new_advance_payment"
          element={userAccount && <AdvancePaymentForm account={userAccount} />}
        />

        <Route
          path="/edit_advance_payment"
          key="edit_advance_payment"
          element={userAccount && <AdvancePaymentForm account={userAccount} />}
        />
      </Routes>
    </Router>
  );
};

export default AppRouter;
